import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import MaterialIcon from "@/components/ui/MaterialIcon";
import Spinner from "@/components/ui/spinner";
import IntercomLogo from "@/components/logos/IntercomLogo";
import SlackLogo from "@/components/logos/SlackLogo";

export type Platform = "intercom" | "slack";

interface PlatformConfig {
  label: string;
  logo: React.ReactNode;
  basePath: string;
}

const PLATFORM_CONFIGS: Record<Platform, PlatformConfig> = {
  intercom: {
    label: "Intercom",
    logo: <IntercomLogo className="size-6" />,
    basePath: "/api/v1/third_parties/intercom",
  },
  slack: {
    label: "Slack",
    logo: <SlackLogo className="size-6" />,
    basePath: "/api/v1/third_parties/slack",
  },
};

interface PlatformConnectionProps {
  platform: Platform;
  onConnectionChange?: (connected: boolean) => void;
}

const PlatformConnection: React.FunctionComponent<PlatformConnectionProps> = ({
  platform,
  onConnectionChange,
}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);

  const config = PLATFORM_CONFIGS[platform];

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const response = await fetch(`${config.basePath}/status`, {
          credentials: "include",
          headers: { Accept: "application/json" },
        });

        const data = await response.json();
        console.log(`[${config.label}] status response:`, data);

        if (response.ok) {
          setIsConnected(data.connected);
          onConnectionChange?.(data.connected);
        }
      } catch (error) {
        console.error(`Failed to check ${config.label} status:`, error);
      } finally {
        setIsCheckingStatus(false);
      }
    };

    checkStatus();
  }, [platform, onConnectionChange, config.basePath, config.label]);

  const handleConnect = async () => {
    setIsLoading(true);

    try {
      const response = await fetch(`${config.basePath}/authorize`, {
        credentials: "include",
        headers: { Accept: "application/json" },
      });

      if (!response.ok) {
        const data = await response.json();
        console.error(
          `Failed to get ${config.label} authorize URL:`,
          data.errors
        );
        return;
      }

      const { url } = await response.json();
      window.location.href = url;
    } catch (error) {
      console.error(`Failed to connect to ${config.label}:`, error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setIsLoading(true);

    try {
      const csrfResponse = await fetch("/csrf", { credentials: "include" });
      const { token: csrfToken } = await csrfResponse.json();

      const response = await fetch(`${config.basePath}/disconnect`, {
        method: "DELETE",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": csrfToken,
        },
      });

      if (!response.ok) {
        const data = await response.json();
        console.error(`Failed to disconnect ${config.label}:`, data.errors);
        return;
      }

      setIsConnected(false);
      onConnectionChange?.(false);
    } catch (error) {
      console.error(`Failed to disconnect ${config.label}:`, error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className={cn(
        "min-h-[160px] flex flex-col items-center justify-center gap-3 rounded-lg border border-border bg-muted/30",
        !isConnected && "border-dashed"
      )}
    >
      <Button
        size="lg"
        variant="outline"
        onClick={handleConnect}
        disabled={isConnected || isLoading || isCheckingStatus}
        className={cn(
          "h-14 px-8 text-base gap-3",
          isConnected &&
            "bg-success hover:bg-success text-success-foreground disabled:opacity-100"
        )}
      >
        {isCheckingStatus ? (
          <>
            <Spinner size={20} />
            Checking connection...
          </>
        ) : isConnected ? (
          <>
            <MaterialIcon icon="check" size={20} />
            Connected to {config.label}
          </>
        ) : isLoading ? (
          <>
            <Spinner size={20} />
            Connecting to {config.label}...
          </>
        ) : (
          <>
            {config.logo}
            Connect {config.label}
          </>
        )}
      </Button>
      {isConnected && (
        <button
          onClick={handleDisconnect}
          disabled={isLoading}
          className="text-sm text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"
        >
          {isLoading ? "Disconnecting..." : `Disconnect ${config.label} Account`}
        </button>
      )}
    </div>
  );
};

export default PlatformConnection;
